import { Components } from "formiojs";
import {
  AsYouType,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/max';
import { advancedPhoneNumberTemplate } from "@/formio-templates/advancedPhoneNumberTemplate";
import { CALLABLE_COUNTRIES } from "@/constants/countries";

const FieldComponent = Components.components.field;

type RefsPartial = {
  readonly input?: HTMLInputElement;
  readonly countryFlag?: HTMLSpanElement;
  readonly countryCode?: HTMLSpanElement;
  readonly countryDropdown?: HTMLDivElement;
  readonly countryDropdownButton?: HTMLButtonElement;
  readonly countryDropdownSearch?: HTMLInputElement;
  readonly countryDropdownList?: HTMLDivElement;
  readonly extensionToggleButton?: HTMLButtonElement;
  readonly extensionWrapper?: HTMLDivElement;
  readonly extensionInput?: HTMLInputElement;
};

export default class AdvancedPhoneNumberComponent extends FieldComponent {
  private currentCountryCode: CountryCode | null = null;
  private asYouType: AsYouType | null = null;
  private isInitialized = false;
  private extensionVisible = false;
  private extensionValue = '';

  static schema() {
    return FieldComponent.schema({
      type: "phone",
      label: "Phone Number",
      key: "phone",
      multiple: false,
      input: true,
      inputType: "tel",
      countryCodePrefillValue: null,
      validate: {
        required: false,
      },
    });
  }

  static builderInfo = {
    title: "Phone Number",
    group: "basic",
    icon: "phone",
    weight: 10,
    schema: AdvancedPhoneNumberComponent.schema(),
  };

  constructor(component: any, options: any, data: any) {
    super(component, options, data);
    this.asYouType = new AsYouType();
  }

  render() {
    return super.render(advancedPhoneNumberTemplate(this));
  }

  attach(element: HTMLElement) {
    const result = super.attach(element);
    
    this.loadRefs(element, {
      input: 'multiple',
      countryFlag: 'single',
      countryCode: 'single',
      countryDropdown: 'single',
      countryDropdownButton: 'single',
      countryDropdownSearch: 'single',
      countryDropdownList: 'single',
      extensionToggleButton: 'single',
      extensionWrapper: 'single',
      extensionInput: 'single',
    });

    if(
      !this.typedRefs.input
      || !this.typedRefs.countryFlag
      || !this.typedRefs.countryCode
      || !this.typedRefs.countryDropdown
      || !this.typedRefs.countryDropdownButton
      || !this.typedRefs.countryDropdownSearch
      || !this.typedRefs.countryDropdownList
      || (this.component.settings?.useExtension && (
        !this.typedRefs.extensionToggleButton
        || !this.typedRefs.extensionWrapper
        || !this.typedRefs.extensionInput
      ))
    ) {
      console.error('Failed to find necessary refs for phone number component');

      return result;
    }

    this.setupComponent();

    // Always sync UI to current internal state on every attach. The freshly
    // rendered DOM starts at the template defaults (e.g. data-country-code="null"),
    // so we must push the in-memory state into it on every (re)attach — even when
    // attach happens without a preceding detach (fast re-render in the builder).
    this.renderCountryList();
    this.syncCountryUI();
    this.syncExtensionUI();
    this.updatePhoneInputUI();
    this.updateExtensionElementsStateUI();

    return result;
  }

  detach() {
    // Reset initialization flag so the component reinitializes on next attach
    this.isInitialized = false;

    return super.detach();
  }

  private setupComponent() {
    if (this.isInitialized) {
      return;
    }

    if (this.component.countryCodePrefillValue && isSupportedCountry(this.component.countryCodePrefillValue)) {
      this.setCountry(this.component.countryCodePrefillValue);
    }

    // Initialize country from existing value
    this.initializeCountryFromValue();

    // Setup event listeners (one-time only)
    this.setupEventListeners();

    this.isInitialized = true;
  }

  /**
   * Pushes the current `currentCountryCode` state into the country flag/code
   * DOM elements. Safe to call multiple times — always writes to the DOM
   * unconditionally so a freshly rendered DOM is brought in sync with state.
   */
  private syncCountryUI() {
    if (!this.typedRefs.countryFlag || !this.typedRefs.countryCode) {
      return;
    }

    const countryCode = this.currentCountryCode;

    if (countryCode) {
      this.typedRefs.countryFlag.innerHTML = this.getCountryFlagHTML(countryCode);
      this.typedRefs.countryFlag.setAttribute('data-country-code', countryCode);
      this.typedRefs.countryCode.textContent = `+${getCountryCallingCode(countryCode)}`;
      this.typedRefs.countryCode.setAttribute('data-calling-code', getCountryCallingCode(countryCode));
    } else {
      this.typedRefs.countryFlag.innerHTML = '<i class="fa fa-globe"></i>';
      this.typedRefs.countryFlag.setAttribute('data-country-code', 'null');
      this.typedRefs.countryCode.textContent = '';
      this.typedRefs.countryCode.setAttribute('data-calling-code', 'null');
    }
  }

  private initializeCountryFromValue() {
    const currentValue = this.getValue();

    if (!currentValue) {
      return;
    }

    // Parse extension from stored value
    const { phoneNumber } = this.parseValue(currentValue);

    try {
      const parsed = parsePhoneNumber(phoneNumber);

      if (parsed?.country) {
        this.setCountry(parsed.country);
        this.handleInputChange(phoneNumber);
      }
    } catch (error) {
      console.warn('Failed to parse phone number:', error);
    }
  }

  private renderCountryList() {
    if (!this.typedRefs.countryDropdownList) {
      return;
    }

    const labelledCountries = CALLABLE_COUNTRIES.map((country) => ({
      ...country,
      name: this.t(country.code),
    }));

    const sortedCountries = [...labelledCountries].sort((a, b) => 
      a.name.localeCompare(b.name)
    );

    this.typedRefs.countryDropdownList.innerHTML = sortedCountries
      .map(
        (country) => {
          return `
            <div class="country-item" role="option" data-country-code="${country.code}" data-calling-code="${country.callingCode}">
              ${this.getCountryFlagHTML(country.code)}
              <span class="country-name">${country.name}</span>
              <span class="country-calling-code">+${country.callingCode}</span>
            </div>
          `;
        }
      )
      .join('');
  }

  private setupEventListeners() {
    if (this.typedRefs.countryDropdownButton) {
      this.addEventListener(
        this.typedRefs.countryDropdownButton,
        'click',
        (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();

          if (!this.typedRefs.countryDropdown || !this.typedRefs.countryDropdownSearch) {
            return;
          }

          const isVisible = this.typedRefs.countryDropdown.style.display !== 'none';

          this.typedRefs.countryDropdown.style.display = isVisible ? 'none' : 'block';

          if (!isVisible) {
            this.typedRefs.countryDropdownSearch.value = '';
            this.filterCountries('');
            this.typedRefs.countryDropdownSearch.focus();
          }
        }
      );
    }

    this.addEventListener(
      document as any as HTMLElement,
      'click',
      (e: MouseEvent) => {
        if (!this.typedRefs.countryDropdownButton || !this.typedRefs.countryDropdown) {
          return;
        }

        if (!this.typedRefs.countryDropdownButton.contains(e.target as Node) && !this.typedRefs.countryDropdown.contains(e.target as Node)) {
          this.typedRefs.countryDropdown.style.display = 'none';
        }
      }
    );

    if (this.typedRefs.countryDropdownSearch) {
      this.addEventListener(
        this.typedRefs.countryDropdownSearch,
        'input',
        (e: InputEvent) => {
          const searchTerm = (e.target as HTMLInputElement).value;

          this.filterCountries(searchTerm);
        }
      );
    }

    if (this.typedRefs.countryDropdown) {
      this.addEventListener(
        this.typedRefs.countryDropdown,
        'click',
        (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          const countryItem = target.closest('.country-item') as HTMLElement;

          if (countryItem) {
            const countryCode = countryItem.dataset.countryCode as CountryCode;

            this.setCountry(countryCode);
            this.handleInputChange(this.typedRefs.input?.value ?? '');

            if (this.typedRefs.countryDropdown) {
              this.typedRefs.countryDropdown.style.display = 'none';
            }

            if (this.typedRefs.input) {
              this.typedRefs.input.focus();
            }
          }
        }
      )
    }

    if (this.typedRefs.input) {
      // Format phone number as user types
      this.addEventListener(
        this.typedRefs.input,
        'input',
        (e: InputEvent) => {
          const target = e.target as HTMLInputElement;
          this.handleInputChange(target.value);
        }
      );

      this.addEventListener(
        this.typedRefs.input,
        'blur',
        () => {
          const displayValue = this.getInputDisplayValue();

          if (!displayValue && this.currentCountryCode) {
            this.setCountry(null);
            this.setValue('', { modified: true });
          }

          // NOTE: Remove extension and hide its input when phone number is cleared
          if (!displayValue) {
            this.extensionVisible = false;
            this.extensionValue = '';
            this.syncExtensionUI();
          }
        }
      );
    }

    // Extension toggle button
    if (this.typedRefs.extensionToggleButton) {
      this.addEventListener(
        this.typedRefs.extensionToggleButton,
        'click',
        (e: MouseEvent) => {
          e.preventDefault();
          this.toggleExtension();
        }
      );
    }

    // Extension input
    if (this.typedRefs.extensionInput) {
      this.addEventListener(
        this.typedRefs.extensionInput,
        'input',
        (e: InputEvent) => {
          const target = e.target as HTMLInputElement;
          const sanitized = target.value.replace(/\D/g, '');

          if (target.value !== sanitized) {
            target.value = sanitized;
          }

          this.extensionValue = sanitized;
          this.handleInputChange(this.getValue());
        }
      );

      this.addEventListener(
        this.typedRefs.extensionInput,
        'keypress',
        (e: KeyboardEvent) => {
          if (e.key.length === 1 && !/\d/.test(e.key)) {
            e.preventDefault();
          }
        }
      );

      this.addEventListener(
        this.typedRefs.extensionInput,
        'paste',
        (e: ClipboardEvent) => {
          const pasted = e.clipboardData?.getData('text') ?? '';
          const sanitized = pasted.replace(/\D/g, '');

          if (pasted !== sanitized) {
            e.preventDefault();

            const input = e.target as HTMLInputElement;
            const start = input.selectionStart ?? input.value.length;
            const end = input.selectionEnd ?? input.value.length;
            const newValue = input.value.slice(0, start) + sanitized + input.value.slice(end);

            input.value = newValue;

            const cursor = start + sanitized.length;

            input.setSelectionRange(cursor, cursor);
            this.extensionValue = newValue;
            this.handleInputChange(this.getValue());
          }
        }
      );

      this.addEventListener(
        this.typedRefs.extensionInput,
        'blur',
        () => {
          if (!this.extensionValue) {
            this.extensionVisible = false;
            this.extensionValue = '';
            this.syncExtensionUI();
          }
        }
      );
    }
  }

  get typedRefs(): RefsPartial {
    return {
      ...this.refs,
      input: ((this.refs as Record<string, any>).input as undefined | HTMLInputElement[])?.[0],
    } as RefsPartial;
  }

  private filterCountries(searchTerm: string) {
    if (!this.typedRefs.countryDropdown) {
      return;
    }

    const countryItems = this.typedRefs.countryDropdown.querySelectorAll('.country-item');
    const lowerSearch = searchTerm.toLowerCase();

    countryItems.forEach((item) => {
      const htmlItem = item as HTMLElement;
      const countryName = htmlItem.querySelector('.country-name')?.textContent || '';
      const countryCode = htmlItem.dataset.countryCode || '';
      const callingCode = htmlItem.dataset.callingCode || '';
      
      const matches = 
        countryName.toLowerCase().includes(lowerSearch) ||
        countryCode.toLowerCase().includes(lowerSearch) ||
        callingCode.includes(lowerSearch);

      htmlItem.style.display = matches ? 'flex' : 'none';
    });
  }

  private setCountry(countryCode: CountryCode | null) {
    if (!this.typedRefs.countryFlag || !this.typedRefs.countryCode) {
      return;
    }

    // Always rebuild the AsYouType formatter when country changes, and always
    // push state into the DOM. We intentionally do not early-return on
    // `currentCountryCode === countryCode`: the DOM may be a freshly rendered
    // element that hasn't yet received the current state (re-attach scenarios).
    this.currentCountryCode = countryCode;
    this.asYouType = new AsYouType(countryCode ?? undefined);

    this.syncCountryUI();
  }

  private updatePhoneInputUI() {
    if (!this.typedRefs.input) {
      return;
    }

    // Update display value (without country code)
    const displayValue = this.getInputDisplayValue();

    if (this.typedRefs.input.value !== displayValue) {
      this.typedRefs.input.value = displayValue;

      // Place cursor at the end
      const newCursorPosition = displayValue.length;
      this.typedRefs.input.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }

  private updateExtensionElementsStateUI() {
    const enabled = this.getInputDisplayValue();

    if (this.typedRefs.extensionToggleButton) {
      this.typedRefs.extensionToggleButton.disabled = !enabled;
    }

    if(this.typedRefs.extensionInput) {
      this.typedRefs.extensionInput.disabled = !enabled;
    }

    this.syncExtensionUI();
  }

  private handleInputChange(value: string) {
    const newValue = this.ensureStartsWithCountryCode(value);
    
    if (this.asYouType) {
      this.asYouType.reset();
      const formatted = this.asYouType.input(newValue);
      const phoneNumberWithExtension = this.getPhoneNumberWithExtension(formatted);

      this.setCountry(this.asYouType.getCountry() ?? null);
      super.setValue(phoneNumberWithExtension, { modified: true });
      this.updatePhoneInputUI();
      this.updateExtensionElementsStateUI();
    }
  }

  private ensureStartsWithCountryCode(inputValue: string): string {
    if (inputValue.startsWith('+') || !this.currentCountryCode) {
      return inputValue;
    }

    return `+${getCountryCallingCode(this.currentCountryCode)} ${inputValue}`;
  }

  private getInputDisplayValue(): string {
    const rawValue = this.getValue() || '';
    const { phoneNumber } = this.parseValue(rawValue);
    const currentCountryCallingCode = this.currentCountryCode
      ? `+${getCountryCallingCode(this.currentCountryCode)}`
      : '';

    return currentCountryCallingCode
      ? phoneNumber.replace(currentCountryCallingCode, '').trimStart()
      : phoneNumber.trimStart();
  }

  private toggleExtension() {
    this.extensionVisible = !this.extensionVisible;

    if (!this.extensionVisible) {
      this.extensionValue = '';

      if (this.typedRefs.extensionInput) {
        this.typedRefs.extensionInput.value = '';
      }
    }

    this.syncExtensionUI();
    this.updatePhoneInputUI();

    if (this.extensionVisible && this.typedRefs.extensionInput) {
      this.typedRefs.extensionInput.focus();
    }
  }

  private syncExtensionUI() {
    if (this.typedRefs.extensionWrapper) {
      this.typedRefs.extensionWrapper.style.display = this.extensionVisible ? 'flex' : 'none';
    }

    if (this.typedRefs.extensionToggleButton) {
      this.typedRefs.extensionToggleButton.style.display = this.extensionVisible ? 'none' : '';
    }

    if (this.typedRefs.extensionInput && this.extensionVisible) {
      this.typedRefs.extensionInput.value = this.extensionValue;
    }
  }

  private getPhoneNumberWithExtension(phoneNumber: string): string {
    if (phoneNumber && this.extensionVisible && this.extensionValue) {
      return `${phoneNumber} ext. ${this.extensionValue}`;
    }

    return phoneNumber;
  }

  private parseValue(value: string): {
    readonly phoneNumber: string;
    readonly extension: string;
  } {
    const extMatch = value.match(/^(.+?)\s+ext\.\s+(.+)$/);

    if (extMatch) {
      return { phoneNumber: extMatch[1], extension: extMatch[2] };
    }

    return { phoneNumber: value, extension: '' };
  }

  private getCountryFlagHTML(countryCode: string): string {
    const countryCodeLowercase = countryCode.toLowerCase();

    return `<img src="https://flagcdn.com/w20/${countryCodeLowercase}.png" srcset="https://flagcdn.com/w40/${countryCodeLowercase}.png 2x" width="20" />`
  }

  checkValidity(data: any, dirty: any | boolean, rowData: any): boolean {
    const isValid = super.checkValidity(data, dirty, rowData);
    const value = this.getValue();

    if (!value) {
      return isValid;
    }

    if (!isValidPhoneNumber(value)) {
      this.setCustomValidity(this.t('Phone number is not valid'), dirty);

      return false;
    }

    return isValid;
  }

  setValue(value: any, flags: any = {}) {
    const result = super.setValue(value, flags);

    // After setting value, try to parse and set country
    if (value && !this.currentCountryCode && this.isInitialized) {
      this.initializeCountryFromValue();
    }

    // Sync extension input from stored value
    if (this.isInitialized) {
      const { extension } = this.parseValue(value || '');

      if (extension && !this.extensionVisible) {
        this.extensionValue = extension;
        this.extensionVisible = true;
        this.syncExtensionUI();
      }
    }

    this.updatePhoneInputUI();
    
    return result;
  }

  getValue() {
    return this.dataValue || '';
  }
}
